import { createRouter, createWebHistory } from 'vue-router'
import { fetchMe, ssoLoginUrl } from '@/api/auth'
import { useUserStore } from '@/store/user'
import { fetchPermissionKeys, hasPermission, clearPermissions } from '@/utils/permission'

/**
 * 路由权限：meta.permission 对应 sys_permission 的 ops:* 菜单 key
 * （种子见后端 docs/sql/ops-demo-roles.sql，角色=运维方案五角色）。
 * 规则引擎/系统管理各页统一挂 ops:rule / ops:monitor / ops:settings（管理类，仅管理员与授权角色可见）。
 */
const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/LoginView.vue'),
    meta: { public: true }
  },
  {
    path: '/',
    name: 'Layout',
    component: () => import('@/views/LayoutView.vue'),
    redirect: '/dashboard',
    children: [
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@/views/DashboardView.vue'),
        meta: { title: '运维看板', icon: 'Odometer', permission: 'ops:dashboard' }
      },
      {
        path: 'work-order',
        name: 'WorkOrder',
        component: () => import('@/views/WorkOrderView.vue'),
        meta: { title: '工单管理', icon: 'Tickets', permission: 'ops:workorder' }
      },
      {
        path: 'review-list',
        name: 'ReviewList',
        component: () => import('@/views/ReviewListView.vue'),
        meta: { title: '待复核清单', icon: 'Checked', permission: 'ops:review' }
      },
      {
        path: 'my-work-order',
        name: 'MyWorkOrder',
        component: () => import('@/views/MyWorkOrderView.vue'),
        meta: { title: '我的工单', icon: 'User', permission: 'ops:myworkorder' }
      },
      {
        path: 'inspection',
        name: 'Inspection',
        component: () => import('@/views/InspectionView.vue'),
        meta: { title: '巡检管理', icon: 'Search', permission: 'ops:inspection' }
      },
      {
        path: 'statistics',
        name: 'Statistics',
        component: () => import('@/views/StatisticsView.vue'),
        meta: { title: '统计分析', icon: 'TrendCharts', permission: 'ops:statistics' }
      },
      {
        path: 'monitor',
        name: 'Monitor',
        component: () => import('@/views/MonitorView.vue'),
        meta: { title: '平台监控', icon: 'Monitor', permission: 'ops:monitor' }
      },
      {
        path: 'work-order-process',
        name: 'WorkOrderProcess',
        component: () => import('@/views/WorkOrderProcessView.vue'),
        meta: { title: '工单流程', icon: 'SetUp', permission: 'ops:process' }
      },
      {
        path: 'settings',
        name: 'Settings',
        component: () => import('@/views/SettingsView.vue'),
        meta: { title: '系统配置', icon: 'Setting', permission: 'ops:settings' }
      },
      {
        path: 'rules',
        name: 'RuleList',
        component: () => import('@/views/RuleList.vue'),
        meta: { title: '阈值规则管理', icon: 'Bell', permission: 'ops:rule' }
      },
      {
        path: 'rules/create',
        name: 'RuleCreate',
        component: () => import('@/views/RuleForm.vue'),
        meta: { title: '新增阈值规则', permission: 'ops:rule' }
      },
      {
        path: 'rules/edit/:id',
        name: 'RuleEdit',
        component: () => import('@/views/RuleForm.vue'),
        meta: { title: '编辑阈值规则', permission: 'ops:rule' }
      },
      {
        path: 'canvas',
        name: 'RuleCanvas',
        component: () => import('@/views/RuleCanvas.vue'),
        meta: { title: '规则画布', icon: 'Grid', permission: 'ops:rule' }
      },
      {
        path: 'templates',
        name: 'RuleTemplate',
        component: () => import('@/views/RuleTemplate.vue'),
        meta: { title: '规则模板库', icon: 'Collection', permission: 'ops:rule' }
      },
      {
        path: 'logs',
        name: 'RuleLog',
        component: () => import('@/views/RuleLog.vue'),
        meta: { title: '规则执行日志', icon: 'Document', permission: 'ops:rule' }
      },
      {
        path: 'alarms',
        name: 'AlarmDashboard',
        component: () => import('@/views/AlarmDashboard.vue'),
        meta: { title: '告警看板', icon: 'Warning', permission: 'ops:alarm' }
      },
      {
        path: 'fuzzy-rules',
        name: 'FuzzyRule',
        component: () => import('@/views/FuzzyRule.vue'),
        meta: { title: '模糊逻辑规则', icon: 'MagicStick', permission: 'ops:rule' }
      },
      {
        path: 'alarm-project-config',
        name: 'AlarmProjectConfig',
        component: () => import('@/views/AlarmProjectConfigView.vue'),
        meta: { title: '告警项目配置', icon: 'Link', permission: 'ops:alarmproject' }
      },
      {
        path: 'quick-report',
        name: 'QuickReport',
        component: () => import('@/views/QuickReportView.vue'),
        meta: { title: '随手拍上报', icon: 'Camera', permission: 'ops:quickreport' }
      },
      {
        path: 'log-manage',
        name: 'LogManage',
        component: () => import('@/views/LogManageView.vue'),
        meta: { title: '日志审计', icon: 'Memo' }
      }
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/'
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

let permissionLoaded = false

/**
 * 登录守卫 + 菜单权限守卫：
 * 1. 未登录整页跳网关 SSO；
 * 2. 登录后拉一次 permission_key 列表（供菜单过滤与 v-permission 使用）；
 * 3. 无该菜单权限时回退到第一个有权限的菜单（子账号只看到随手拍）。
 */
/* SSO 重定向循环熔断：60 秒窗口内最多自动跳 3 次，超过则停止跳转并给出可见提示，
 * 避免后端重启/回调出错时守卫无限循环白屏（清 Cookie 或点重试后计数归零） */
const SSO_LOOP_KEY = 'sso:redirects'

function ssoLoopExceeded() {
  const now = Date.now()
  const hits = JSON.parse(sessionStorage.getItem(SSO_LOOP_KEY) || '[]').filter((t) => now - t < 60000)
  hits.push(now)
  sessionStorage.setItem(SSO_LOOP_KEY, JSON.stringify(hits))
  return hits.length > 3
}

function ssoErrorText(code) {
  return {
    missing_code: '回调缺少授权码',
    invalid_state: '登录会话状态失效（服务可能刚重启）',
    token_exchange_failed: '授权码换取令牌失败（认证服务异常或刚重启）'
  }[code] || '未知错误: ' + code
}

router.beforeEach(async (to, from, next) => {
  if (to.meta?.public) {
    next()
    return
  }
  // SSO 回调失败时网关会落回本页并带 sso_error 参数：向用户展示原因，不再自动循环跳转
  if (to.query.sso_error) {
    clearPermissions()
    sessionStorage.removeItem(SSO_LOOP_KEY)
    next(false)
    alert('SSO 登录失败：' + ssoErrorText(to.query.sso_error) + '，请刷新页面重试')
    return
  }
  let user
  try {
    user = await fetchMe()
  } catch (e) {
    /* 未登录继续走 SSO */
  }
  if (user) {
    sessionStorage.removeItem(SSO_LOOP_KEY)
  } else {
    clearPermissions()
    // 循环熔断：短时间多次跳转 SSO 仍未登录成功，停止自动跳转避免无限白屏；刷新页面即重试
    if (ssoLoopExceeded()) {
      alert('已多次尝试 SSO 登录仍未成功（认证服务可能刚重启）。请稍候刷新页面重试，或清除浏览器 Cookie 后重新登录。')
      next(false)
      return
    }
    // 整页跳网关登录
    window.location.href = ssoLoginUrl(to.fullPath)
    return
  }
  const userStore = useUserStore()
  userStore.setUserInfo(user)
  if (!permissionLoaded) {
    await fetchPermissionKeys()
    permissionLoaded = true
  }
  if (to.meta?.permission && !hasPermission(to.meta.permission)) {
    const fallback = router.options.routes
      .flatMap((r) => r.children || [r])
      .find((r) => r.meta?.permission && hasPermission(r.meta.permission))
    next(fallback ? fallback.path : '/quick-report')
    return
  }
  next()
})

export function resetPermissionLoaded() {
  permissionLoaded = false
}

export default router
